// src/components/player/PlayerContractTab.js - REDESIGNED to match Overview tab style
// Layout: Full-width Contract Details panel + Triptych (Value Analysis | Team Details | Contract Terms)
import React from 'react';
import { dynastyTheme } from '../../services/colorService';
import { formatContractDisplay } from '../../services/contractUtils';

// ═══════════════════════════════════════════════════════════════
// INLINE STYLES — Matches PlayerOverviewTab panel style exactly
// ═══════════════════════════════════════════════════════════════
const styles = {
  panel: {
    background: 'rgba(23,23,23,0.5)',
    borderRadius: 5,
    border: '1px solid rgba(55,65,81,0.5)',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '6px 12px 5px',
    borderBottom: '1px solid rgba(55,65,81,0.2)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  panelTitle: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1.1px',
    textTransform: 'uppercase',
    color: '#eab308',
  },
  panelSub: {
    fontSize: 9,
    color: '#404040',
  },
  triptych: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 6,
    marginTop: 6,
  },
  listContainer: {
    padding: '8px 14px 10px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 0',
    borderBottom: '1px solid rgba(55,65,81,0.08)',
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: '#a3a3a3',
  },
  value: {
    fontSize: 13,
    fontWeight: 600,
    color: '#ffffff',
  },
  // Top metrics grid
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 0,
    padding: '10px 14px',
  },
  metricCell: {
    textAlign: 'center',
    padding: '4px 0',
  },
  metricValue: {
    fontSize: 16,
    fontWeight: 700,
    color: '#ffffff',
  },
  metricLabel: {
    fontSize: 10,
    fontWeight: 500,
    color: '#525252',
    marginTop: 2,
  },
};

const PlayerContractTab = ({ player, leagueStatus }) => {
  const leagueContext = player?.league_context;
  const isOwned = leagueContext?.status === 'owned';

  // Check for retained OP contract on free agents.
  // Phase 1.2 fix (2026-05-20, Rule 9): canonical field is `salary` (the
  // active contract's stored salary). Drop the contract_salary dual-read.
  // Gate: render the retained-contract panel only if salary is a positive
  // number (null/undefined/0 → don't render; nothing to show).
  const faFinancial = player?.league_context?.financial || {};
  const faContractType = faFinancial?.contract_type || '';
  const hasRetainedContract = !isOwned && faContractType.startsWith('OP') && (faFinancial?.salary > 0);

  if (!isOwned && !hasRetainedContract) {
    const txnHistory = leagueContext?.transaction_history;
    const hasTxnHistory = txnHistory && txnHistory.length > 0;

    return (
      <div>
        <div style={{ padding: '20px 20px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#a3a3a3' }}>This player is a free agent</div>
        </div>

        {/* Transaction history — show even for free agents */}
        {hasTxnHistory && (
          <div style={{ ...styles.panel, margin: '8px 0' }}>
            <div style={styles.panelHeader}>
              <span style={styles.panelTitle}>Transaction History</span>
              <span style={styles.panelSub}>{txnHistory.length} transactions</span>
            </div>
            <div style={{ padding: '4px 14px 10px', maxHeight: 320, overflowY: 'auto' }}>
              {txnHistory.map((txn, i) => {
                const date = txn.transaction_date ? new Date(txn.transaction_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                const typeLabels = {
                  free_agent: 'Signed as Free Agent',
                  fa_acquisition: 'Signed as Free Agent',
                  commissioner_add: 'Added (Commissioner)',
                  release: 'Released',
                  trade: 'Traded',
                  draft_pick: 'Drafted',
                  autodraft: 'Autodrafted',
                  minor_draft_pick: 'Minor League Draft',
                  waiver: 'Waiver Claim',
                };
                const typeLabel = typeLabels[txn.transaction_type] || txn.transaction_type;

                return (
                  <div key={txn.transaction_id || i} style={{ padding: '8px 0', borderBottom: i < txnHistory.length - 1 ? '1px solid rgba(55,65,81,0.15)' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#ffffff' }}>{typeLabel}</span>
                      <span style={{ fontSize: 10, color: '#525252' }}>{date}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#a3a3a3', lineHeight: 1.5 }}>
                      {txn.to_team_name && <div>To: <span style={{ color: '#d4d4d4' }}>{txn.to_team_name}</span></div>}
                      {txn.from_team_name && txn.transaction_type === 'release' && <div>From: <span style={{ color: '#d4d4d4' }}>{txn.from_team_name}</span></div>}
                      {txn.salary > 0 && <div>Salary: <span style={{ color: '#22c55e' }}>${Math.round(txn.salary)}M</span>{txn.contract_years > 0 && <span> | {txn.contract_years} year{txn.contract_years !== 1 ? 's' : ''}</span>}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (hasRetainedContract) {
    // Phase 1.2 fix (2026-05-20, Rule 9): canonical field is `salary`.
    // No dual-read fallback to contract_salary. No `|| 0` fabrication —
    // missing salary renders as the codebase's empty convention `'--'`
    // (matches the owned-contract metric cells at L220 / L240 / L274 / L329).
    const retainedSalary = faFinancial.salary;
    return (
      <div style={styles.panel}>
        <div style={styles.panelHeader}>
          <span style={styles.panelTitle}>Retained Contract</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', padding: '1px 6px', background: 'rgba(239,68,68,0.15)', borderRadius: 3, border: '1px solid rgba(239,68,68,0.3)' }}>
            {faContractType}
          </span>
        </div>
        <div style={styles.listContainer}>
          <div style={styles.row}>
            <span style={styles.label}>Salary</span>
            <span style={{ ...styles.value, color: retainedSalary != null ? '#22c55e' : '#525252' }}>
              {retainedSalary != null ? `$${Math.round(retainedSalary)}` : '--'}
            </span>
          </div>
          <div style={styles.row}>
            <span style={styles.label}>Years Remaining</span>
            <span style={styles.value}>{faFinancial.contract_years ?? '--'}</span>
          </div>
          <div style={{ ...styles.row, borderBottom: 'none' }}>
            <span style={{ fontSize: 11, color: '#525252', fontStyle: 'italic' }}>
              Acquiring team inherits this contract
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Always read from league_context.financial
  const financial = leagueContext.financial || {};
  const team = leagueContext.team || {};
  const roster = leagueContext.roster || {};

  // Owned-player path: the /modal SQL serializer
  // (player_repository.get_player_for_modal / get_player_in_league_context)
  // renames the underlying roster_data.financial.salary field to
  // contract_salary in the response when team_id IS NOT NULL. This is the
  // backend-exposed field name for the owned branch — not a fallback
  // defect. The FA branch exposes the field as `salary` (matches Tony's
  // canonical decision for retained-contract surfacing). Verified
  // empirically 2026-05-20.
  const salary = financial.contract_salary;
  const years = financial.contract_years;
  const contractType = financial.contract_type || '';
  const totalValue = salary != null && years != null ? salary * years : null;
  const baselinePrice = financial.baseline_price;
  const currentValue = financial.current_market_value;
  const changePct = baselinePrice > 0 ? ((currentValue - baselinePrice) / baselinePrice) * 100 : 0;
  const surplus = currentValue != null && salary != null ? currentValue - salary : null;
  const extensionEligible = years <= 1;
  const contractDisplay = formatContractDisplay(contractType, years);

  const statusColor = roster?.status === 'active' ? '#22c55e'
    : roster?.status === 'bench' ? '#eab308'
    : roster?.status === 'dl' ? '#ef4444'
    : roster?.status === 'minors' ? '#3b82f6'
    : '#a3a3a3';

  return (
    <div>
      {/* Row 1: Contract Details — full-width metrics bar */}
      <div style={styles.panel}>
        <div style={styles.panelHeader}>
          <span style={styles.panelTitle}>Contract Details</span>
          {contractType && (
            <span style={{
              fontSize: 10, fontWeight: 700,
              padding: '1px 6px', borderRadius: 3,
              background: contractType.startsWith('OP') ? 'rgba(239,68,68,0.15)' : 'rgba(234,179,8,0.15)',
              color: contractType.startsWith('OP') ? '#ef4444' : '#eab308',
              border: `1px solid ${contractType.startsWith('OP') ? 'rgba(239,68,68,0.3)' : 'rgba(234,179,8,0.3)'}`,
            }}>
              {contractDisplay}
            </span>
          )}
        </div>
        <div style={styles.metricsGrid}>
          <div style={styles.metricCell}>
            <div style={{ ...styles.metricValue, color: '#22c55e' }}>
              {salary != null ? `$${Math.round(salary)}` : '--'}
            </div>
            <div style={styles.metricLabel}>Salary</div>
          </div>
          <div style={styles.metricCell}>
            <div style={styles.metricValue}>{contractDisplay || '--'}</div>
            <div style={styles.metricLabel}>Contract</div>
          </div>
          <div style={styles.metricCell}>
            <div style={styles.metricValue}>{years ?? '--'}</div>
            <div style={styles.metricLabel}>Years Left</div>
          </div>
          <div style={styles.metricCell}>
            <div style={{ ...styles.metricValue, color: extensionEligible ? '#22c55e' : '#525252' }}>
              {extensionEligible ? 'Yes' : 'No'}
            </div>
            <div style={styles.metricLabel}>Extension</div>
          </div>
          <div style={styles.metricCell}>
            <div style={{ ...styles.metricValue, color: '#eab308' }}>
              {baselinePrice != null ? `$${Math.round(baselinePrice)}` : '--'}
            </div>
            <div style={styles.metricLabel}>Base Price</div>
          </div>
          <div style={styles.metricCell}>
            <div style={{ ...styles.metricValue, color: currentValue > 0 ? '#22c55e' : '#525252' }}>
              {currentValue > 0 ? `$${Math.round(currentValue)}` : 'N/A'}
            </div>
            <div style={styles.metricLabel}>Market Value</div>
          </div>
        </div>
      </div>

      {/* Row 2: Triptych */}
      <div style={styles.triptych}>
        {/* Column 1: Value Analysis */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitle}>Value Analysis</span>
          </div>
          <div style={styles.listContainer}>
            <div style={styles.row}>
              <span style={styles.label}>Baseline Price</span>
              <span style={styles.value}>{baselinePrice != null ? `$${Math.round(baselinePrice)}` : '--'}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Market Value</span>
              <span style={{ ...styles.value, color: currentValue > 0 ? '#22c55e' : '#525252' }}>
                {currentValue > 0 ? `$${Math.round(currentValue)}` : 'N/A'}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Surplus vs Salary</span>
              <span style={{ ...styles.value, color: surplus > 0 ? '#22c55e' : surplus < 0 ? '#ef4444' : '#525252' }}>
                {surplus != null && !isNaN(surplus) ? `${surplus > 0 ? '+' : ''}$${Math.round(surplus)}` : '--'}
              </span>
            </div>
            <div style={{ ...styles.row, borderBottom: 'none' }}>
              <span style={styles.label}>Value Change</span>
              <span style={{ ...styles.value, color: changePct > 0 ? '#22c55e' : changePct < 0 ? '#ef4444' : '#525252' }}>
                {baselinePrice > 0 ? `${changePct > 0 ? '+' : ''}${changePct.toFixed(1)}%` : '--'}
              </span>
            </div>
          </div>
        </div>

        {/* Column 2: Team Details */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitle}>Team Details</span>
          </div>
          <div style={styles.listContainer}>
            <div style={styles.row}>
              <span style={styles.label}>Fantasy Team</span>
              <span style={{ ...styles.value, color: '#3b82f6' }}>{team?.team_name || 'Free Agent'}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Owner</span>
              <span style={styles.value}>{team?.owner_name || 'Available'}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Status</span>
              <span style={{
                fontSize: 10, fontWeight: 700,
                padding: '1px 8px', borderRadius: 3,
                background: `${statusColor}20`,
                color: statusColor,
                border: `1px solid ${statusColor}40`,
                textTransform: 'uppercase',
              }}>
                {roster?.status || 'active'}
              </span>
            </div>
            <div style={{ ...styles.row, borderBottom: 'none' }}>
              <span style={styles.label}>Acquired</span>
              <span style={{ ...styles.value, fontSize: 12 }}>{roster?.acquisition_method || 'Unknown'}</span>
            </div>
          </div>
        </div>

        {/* Column 3: Contract Terms */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitle}>Contract Terms</span>
          </div>
          <div style={styles.listContainer}>
            <div style={styles.row}>
              <span style={styles.label}>Annual Salary</span>
              <span style={{ ...styles.value, color: '#22c55e' }}>
                {salary != null ? `$${Math.round(salary)}` : '--'}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Total Value</span>
              <span style={{ ...styles.value, color: '#eab308' }}>
                {totalValue != null ? `$${Math.round(totalValue)}` : '--'}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Contract Type</span>
              <span style={{
                fontSize: 10, fontWeight: 700,
                padding: '1px 8px', borderRadius: 3,
                background: contractType.startsWith('OP') ? 'rgba(239,68,68,0.15)' : 'rgba(234,179,8,0.15)',
                color: contractType.startsWith('OP') ? '#ef4444' : '#eab308',
                border: `1px solid ${contractType.startsWith('OP') ? 'rgba(239,68,68,0.3)' : 'rgba(234,179,8,0.3)'}`,
              }}>
                {contractDisplay || '--'}
              </span>
            </div>
            <div style={{ ...styles.row, borderBottom: 'none' }}>
              <span style={styles.label}>Extension Options</span>
              <span style={{ fontSize: 12, color: extensionEligible ? '#a3a3a3' : '#525252' }}>
                {extensionEligible
                  ? (() => {
                      if (!contractType?.startsWith('OP')) return 'OP';
                      if (contractType === 'OP') return 'OP1, OP2';
                      const n = parseInt(contractType.slice(2)) || 0;
                      return Array.from({ length: Math.max(1, 2) }, (_, i) => `OP${n + i + 1}`).join(', ');
                    })()
                  : 'Not eligible'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Transaction History */}
      {player?.league_context?.transaction_history?.length > 0 && (
        <div style={{ ...styles.panel, marginTop: 8 }}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitle}>Transaction History</span>
            <span style={styles.panelSub}>{player.league_context.transaction_history.length} transactions</span>
          </div>
          <div style={{ padding: '4px 14px 10px', maxHeight: 320, overflowY: 'auto' }}>
            {player.league_context.transaction_history.map((txn, i) => {
              const date = txn.transaction_date ? new Date(txn.transaction_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
              const isTrade = txn.transaction_type === 'trade';
              const typeLabels = {
                free_agent: 'Signed as Free Agent',
                commissioner_add: 'Added (Commissioner)',
                release: 'Released',
                trade: 'Traded',
                trade_reversal: 'Trade Reversed',
                draft_pick: 'Drafted (Live Draft)',
                autodraft: 'Auto-Drafted',
                minor_draft_pick: 'Minor League Draft Pick',
                minor_promotion: 'Promoted from Minors',
                commissioner_salary_override: 'Salary Override',
                waiver_claim: 'Waiver Claim',
                keeper_retained: 'Keeper Retained',
              };
              const typeLabel = typeLabels[txn.transaction_type] || txn.transaction_type;

              const formatPackage = (pkg) => {
                if (!pkg) return [];
                const items = [];
                if (pkg.player_names?.length) {
                  pkg.player_names.forEach(n => items.push(n));
                } else if (pkg.players?.length) {
                  pkg.players.forEach(pid => items.push(`Player ${pid}`));
                }
                if (pkg.picks?.length) {
                  pkg.picks.forEach(p => items.push(`${p.year} Round ${p.round} Pick`));
                }
                if (pkg.cash > 0) items.push(`$${pkg.cash}M cash`);
                return items;
              };

              return (
                <div key={txn.transaction_id || i} style={{ padding: '8px 0', borderBottom: i < player.league_context.transaction_history.length - 1 ? '1px solid rgba(55,65,81,0.15)' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#ffffff' }}>{typeLabel}</span>
                    <span style={{ fontSize: 10, color: '#525252' }}>{date}</span>
                  </div>

                  {isTrade ? (
                    <div style={{ fontSize: 11, color: '#a3a3a3', lineHeight: 1.5 }}>
                      <div style={{ marginBottom: 3 }}>
                        <span style={{ fontWeight: 600, color: '#eab308' }}>{txn.trade_proposing_team}</span>
                        <span> received: </span>
                        <span style={{ color: '#d4d4d4' }}>{formatPackage(txn.trade_receiving_gives).join(', ') || 'nothing'}</span>
                      </div>
                      <div>
                        <span style={{ fontWeight: 600, color: '#eab308' }}>{txn.trade_receiving_team}</span>
                        <span> received: </span>
                        <span style={{ color: '#d4d4d4' }}>{formatPackage(txn.trade_proposing_gives).join(', ') || 'nothing'}</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: '#a3a3a3', lineHeight: 1.5 }}>
                      {txn.to_team_name && <div>Team: <span style={{ color: '#d4d4d4' }}>{txn.to_team_name}</span>{txn.to_owner_name && <span style={{ color: '#525252' }}> ({txn.to_owner_name})</span>}</div>}
                      {txn.from_team_name && txn.transaction_type === 'release' && <div>From: <span style={{ color: '#d4d4d4' }}>{txn.from_team_name}</span></div>}
                      {txn.salary > 0 && <div>Salary: <span style={{ color: '#22c55e' }}>${Math.round(txn.salary)}M</span>{txn.contract_years > 0 && <span> | {txn.contract_years} year{txn.contract_years !== 1 ? 's' : ''}</span>}</div>}
                    </div>
                  )}

                  {txn.is_commissioner_override && (
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 4px', borderRadius: 2, background: 'rgba(234,179,8,0.12)', color: '#eab308', marginTop: 3, display: 'inline-block' }}>Commissioner</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section 3: Financial Summary */}
      {player?.league_context?.transaction_history?.length > 0 && (
        <div style={{ ...styles.panel, marginTop: 8 }}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitle}>Financial Summary</span>
          </div>
          <div style={styles.listContainer}>
            <div style={styles.row}>
              <span style={styles.label}>Current Salary</span>
              <span style={{ ...styles.value, color: '#22c55e' }}>{salary != null ? `$${Math.round(salary)}` : '--'}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Original Acquisition Price</span>
              <span style={styles.value}>
                {(() => {
                  const firstAcq = [...(player.league_context.transaction_history || [])].reverse().find(t => t.salary > 0 && ['free_agent', 'draft_pick', 'autodraft', 'minor_promotion', 'commissioner_add'].includes(t.transaction_type));
                  return firstAcq ? `$${Math.round(firstAcq.salary)}` : '--';
                })()}
              </span>
            </div>
            <div style={{ ...styles.row, borderBottom: 'none' }}>
              <span style={styles.label}>Years Remaining</span>
              <span style={styles.value}>{years ?? '--'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerContractTab;
